import { z } from 'zod';
/**
 * `/health/live` — unconditional liveness. Returns 200 as long as the event
 * loop is responsive. Consumed by ACA startup/liveness probes; not exposed
 * to UI clients (no hook file emitted for `health`).
 */
export const liveResponseSchema = z.object({
    status: z.literal('ok'),
});
/**
 * Per-dependency state surfaced in the `/health/ready` response body for
 * operator visibility. `'ready'` is healthy; `'starting'` is the transient
 * boot window; `'failed'` is the SDK-boot-threw terminal state. The
 * response code is NOT gated on this value — see
 * `marketplace-api/src/modules/health/routes.ts` for the decoupling
 * rationale. (Was: 503 when state !== 'ready'. Coupling the readiness
 * probe to App Insights SDK availability caused the 2026-05-12 staging
 * deploy cascade where every replica returned 503 after the
 * applicationinsights v2→v3 upgrade broke `defaultClient`.)
 */
export const dependencyStateSchema = z.union([
    z.literal('ready'),
    z.literal('starting'),
    z.literal('failed'),
]);
/**
 * `/health/ready` — readiness probe. Currently always returns 200 with
 * stubbed `db: true` / `redis: true`; the body's `appInsights` field is
 * the real signal. Once real DB + Redis probes land (G2-T1 / G2-T7), this
 * route MAY start returning 503 when those legitimate fail-stop deps are
 * unreachable. The schema permits both shapes (`status: 'ok' | 'degraded'`)
 * to leave room for that follow-up without a contract bump.
 */
export const readyResponseSchema = z.object({
    status: z.union([z.literal('ok'), z.literal('degraded')]),
    db: z.boolean(),
    redis: z.boolean(),
    appInsights: dependencyStateSchema,
    /**
     * Effective telemetry sample percentage (1..100), resolved from
     * `MARKETPLACE_TELEMETRY_SAMPLE_PCT`. Lets operators confirm the live
     * sampling rate from the readiness body without an ARM lookup — 100 means
     * full capture. See `docs/runbooks/app-insights-verification.md`.
     */
    sampleRatePct: z.number().int().min(1).max(100),
});
//# sourceMappingURL=health.js.map